import BaseManager from "@base/baseManager.js";
import api from "@api/api.js";
import { foldTag } from "@/services/tags.js";

/**
 * The tag vocabulary: one row per tag, carrying the key that galleries store
 * and the display name for each language.
 *
 * Rows arrive already filtered to what the viewer may see (tagService
 * .listForViewer), so an anonymous visitor's filter bar can't name a tag that
 * only a private gallery carries.
 */
export default class TagManager extends BaseManager {
  constructor() {
    super("key");
  }

  async load() {
    const res = await api.get("/api/tags");
    return this.set(res?.tags || []);
  }

  /** Display name for a key in one language; the key itself if it has no row. */
  label(key, lang) {
    const row = this.get(key);
    if (!row) return key;
    return (lang === "de" ? row.nameDe : row.nameEn) || key;
  }

  /** `{ key: label }` for one language — cheap to hand to a view as a prop. */
  labels(lang) {
    return Object.fromEntries(this.all().map((row) => [row.key, this.label(row.key, lang)]));
  }

  /**
   * Vocabulary for the editor's tag field. `folded` carries the key *and* both
   * names, so typing "Landschaft" finds the tag whose key is "Landscape" —
   * without that, the German name would be invisible to the one field where
   * knowing it matters most.
   */
  vocabulary(lang) {
    return this.all()
      .map((row) => ({
        key: row.key,
        label: this.label(row.key, lang),
        count: row.count || 0,
        folded: [...new Set([row.key, row.nameEn, row.nameDe].filter(Boolean).map(foldTag))],
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }
}
